import { Action } from './actions';
import { Params } from './backend';
import { RootModel, RootModelLists, RootModelObjects } from './root.model';

export interface RawEntity<E> {
  entity: E;
  timeFetched: number;
  timeUpdated: number;
}

export interface RawEntities<E> {
  [id: string]: RawEntity<E>;
}

export interface Lists {
  [query: string]: string[];
}

export interface Reference<T extends keyof RootModel> {
  path: Path<T>;
  query?: Query;
}

export interface ReferenceList<T extends keyof RootModel> {
  pathList: PathList<T>;
  query?: Query;
}

export interface Pagination {
  limit?: number;
  offset?: number;
}

export type Path<T extends keyof RootModel> = [T, string];
export type PathList<T extends keyof RootModel> = [T, string[]];

export function pathToPathList<T extends keyof RootModel>(
  path: Path<T>,
): PathList<T> {
  return [path[0], [path[1]]];
}

export function firstPathFromPathList<T extends keyof RootModel>(
  pathList: PathList<T>,
): Path<T> {
  return [pathList[0], pathList[1][0]];
}

export function referenceToReferenceList<T extends keyof RootModel>(
  ref: Reference<T>,
): ReferenceList<T> {
  return { pathList: pathToPathList(ref.path) };
}

export function firstReferenceFromReferenceList<T extends keyof RootModel>(
  refList: ReferenceList<T>,
): Reference<T> {
  return { path: firstPathFromPathList(refList.pathList) };
}

// Queries that you can pass to an endpoint.
export type Query = Pagination & { params?: Params };

export interface RawEntityList<T> {
  entities: RawEntities<T>;
  lists: Lists;
}

export type RawStoreObjectStateSlice<
  T extends keyof RootModelObjects
> = RootModelObjectToRawStoreStateSlice<RootModelObjects[T]>;

export type RawStoreListStateSlice<
  T extends keyof RootModelLists
> = RootModelListToRawStoreStateSlice<RootModelLists[T]>;

type RawStoreObjectState = RootModelObjectToRawStoreObjectState<
  RootModelObjects
>;
export type RawStoreListState = RootModelListToRawStoreListState<
  RootModelLists
>;
export type RawStoreState = RawStoreListState & RawStoreObjectState;

export type RootModelEntity<T extends keyof RootModel> = (RootModelLists &
  RootModelObjects)[T][string];

export type ObjectReducer<T extends keyof RootModelObjects> = (
  state: RawStoreObjectStateSlice<T>,
  action: Action,
) => RawStoreObjectStateSlice<T>;

export type ListReducer<T extends keyof RootModelLists> = (
  state: RawStoreListStateSlice<T>,
  action: Action,
) => RawStoreListStateSlice<T>;

type RootModelObjectToRawStoreStateSlice<T> = {
  [P in keyof T]: RawEntity<T[P]>
};

type RootModelListToRawStoreStateSlice<T> = {
  [P in keyof T]: RawEntityList<T[P]>
};

type RootModelObjectToRawStoreObjectState<T> = {
  [P in keyof T]: { [Q in keyof T[P]]: RawEntity<T[P][Q]> }
};

type RootModelListToRawStoreListState<T> = {
  [P in keyof T]: { [Q in keyof T[P]]: RawEntityList<T[P][Q]> }
};

type RootModelToRawStoreEntities<
  T,
  P extends keyof T,
  Q extends keyof T[P]
> = RawEntities<T[P][Q]>;

function arrayShift<T>(a: T[], offset: number) {
  return [...new Array<T>(offset), ...a];
}

function compare<T>(a: T, b: T) {
  return a < b ? -1 : a > b ? 1 : 0;
}

function arrayMerge<T>(a: T[], b: T[]) {
  const result = [];
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    if (a[i] || b[i]) {
      result[i] = a[i] || b[i];
    }
  }
  return result;
}

/* This merges a number of entities into the state.
 * If an offset it specified then the new entities are placed at that offset.
 * If an offset is not specified then the new entities will be:
 *   - merged if they have the same id as an existing entity OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 */
export function mergeEntities<T extends keyof RootModelLists>(
  state: RawStoreListState[T],
  entities: RootModelToRawStoreEntities<RootModelLists, T, string>,
  ref: Reference<T>,
  sortField?: (entity: RootModelLists[T][string]) => number | string,
): RawStoreListState[T] {
  const data =
    state[ref.path[1]] && state[ref.path[1]].entities
      ? state[ref.path[1]]
      : { entities: {}, lists: {} };
  const query = 'default'; // TODO: T37424
  const originalList =
    data.lists && data.lists[query] ? [...data.lists[query]] || [] : [];

  const updatedEntities = {
    ...data.entities,
    ...entities,
  };

  const newEntityIds = Object.keys(updatedEntities).filter(
    key => !Object.keys(data.entities).includes(key),
  );

  // If the new list doesn't have an offset then the new list is put at the front.
  const originalListOffset =
    ref.query && ref.query.offset ? 0 : newEntityIds.length;
  const newListOffset = (ref.query && ref.query.offset) || 0;

  const updatedQueryList = arrayMerge(
    arrayShift(originalList, originalListOffset),
    arrayShift(newEntityIds, newListOffset),
  );

  if (sortField) {
    updatedQueryList.sort((a, b) =>
      compare(
        sortField(updatedEntities[a].entity),
        sortField(updatedEntities[b].entity),
      ),
    );
  }
  return Object.assign({}, state, {
    [ref.path[1]]: {
      entities: updatedEntities,
      lists: {
        ...data.lists,
        ...{ [query]: updatedQueryList },
      },
    },
  });
}

export function mergeListEntities<T extends keyof RootModelLists>(
  state: RawStoreListState[T],
  entities: RootModelToRawStoreEntities<RootModelLists, T, string>,
  refList: ReferenceList<T>,
  groupKey: keyof RootModelLists[T][string],
  sortField?: (entity: RootModelLists[T][string]) => number | string,
): RawStoreListState[T] {
  return refList.pathList[1].reduce((newState, section) => {
    const ref: Reference<T> = { path: [refList.pathList[0], section] };
    const theseEntities = objectFilter(
      entities,
      (id, rawEntity) => String(rawEntity.entity[groupKey]) === section,
    );

    return mergeEntities(newState, theseEntities, ref, sortField);
  }, state);
}

function entityToRawEntity<E>(entity: E): RawEntity<E> {
  return {
    entity,
    timeFetched: Date.now(),
    timeUpdated: Date.now(),
  };
}

export function mergeObject<T extends keyof RootModelObjects>(
  state: RawStoreObjectState[T],
  object: RootModelObjects[T]['string'],
  ref: Reference<T>,
): RawStoreObjectState[T] {
  return Object.assign({}, state, { [ref.path[1]]: entityToRawEntity(object) });
}

export function mergeMultipleObjects<T extends keyof RootModelObjects>(
  state: RawStoreObjectState[T],
  objects: (RootModelObjects[T]['string'] & { id: number | string })[],
): RawStoreObjectState[T] {
  const newState: RawStoreObjectState[T] = objects.reduce(
    (acc: RawStoreObjectState[T], e) =>
      Object.assign({}, acc, { [e.id]: entityToRawEntity(e) }),
    {},
  );

  return Object.assign({}, state, newState);
}

export function transformObjects<
  T extends { id: number | string; type?: string },
  O
>(
  list: O[] | { [id: number]: O } | undefined,
  transform: (a: O) => T | undefined,
): RawEntities<T>;
export function transformObjects<
  T extends { id: number | string; type?: string },
  O,
  E
>(
  list: O[] | { [id: number]: O } | undefined,
  transform: (a: O, extra: E) => T | undefined,
  extra: E,
): RawEntities<T>;
export function transformObjects<
  T extends { id: number | string; type?: string },
  O,
  E
>(
  list: O[] | { [id: number]: O } | undefined,
  transform: (a: O, extra?: E) => T | undefined,
  extra?: E,
): RawEntities<T> {
  if (!list) {
    return {};
  }

  const actualList = Array.isArray(list) ? list : Object.values<O>(list);

  return actualList
    .map(o => transform(o, extra))
    .filter(isDefined)
    .map(entity => ({
      [entity.type ? `${entity.type}-${entity.id}` : entity.id]: {
        entity,
        timeFetched: Date.now(),
        timeUpdated: Date.now(),
      },
    }))
    .reduce((acc: RawEntities<T>, item) => ({ ...acc, ...item }), {});
}

export function pathListEqual<T extends keyof RootModel>(
  a: PathList<T>,
  b: PathList<T>,
) {
  return a[0] === b[0] && sameElements(a[1], b[1]);
}

export function referenceListsEqual<T extends keyof RootModel>(
  a: ReferenceList<T>,
  b: ReferenceList<T>,
) {
  return (
    pathListEqual(a.pathList, b.pathList) &&
    ((a.query === undefined && b.query === undefined) ||
      (a.query !== undefined &&
        b.query !== undefined &&
        a.query.offset === b.query.offset &&
        a.query.limit === b.query.limit &&
        a.query.params === b.query.params))
  );
}

export function sameElements<T>(as: T[], bs: T[]) {
  const setAs = new Set(as);
  const setBs = new Set(bs);
  return as.every(a => setBs.has(a)) && bs.every(b => setAs.has(b));
}

// All the (distinct) elements in `as` that isn't in `bs`.
export function setDiff<T>(as: T[], bs: T[]) {
  const setAs = new Set(as);
  const setBs = new Set(bs);
  return Array.from(setAs).filter(a => !setBs.has(a));
}

export function assertNever(
  x: never,
  error: string = 'Unexpected object',
): never {
  throw new Error(`${error}: ${JSON.stringify(x)}`);
}

export function uniq<T>(
  xs: T[],
  compareFunction: (a: T, b: T) => boolean = (a: T, b: T) => a === b,
): T[] {
  const compareWith = (x: T) => (_: T) => compareFunction(x, _);
  return xs.reduce(
    (acc: T[], x: T) => (acc.find(compareWith(x)) ? acc : [...acc, x]),
    [],
  );
}

export function isDefined<T>(x?: T): x is T {
  return x !== undefined;
}

export function objectFilter<V>(
  object: { [key: string]: V },
  predicate: (key: string, v: V) => boolean,
) {
  return Object.entries(object)
    .filter(([key, value]) => predicate(key, value))
    .reduce((obj: { [key: string]: V }, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
}

export function objectFilterByKeys<V>(
  object: { [key: string]: V },
  keys: string[],
) {
  return objectFilter(object, key => keys.includes(key));
}

export function pluckListItemFromRawStore<T extends keyof RootModelLists>(
  store: RawStoreListState,
  path: Path<T>,
  id: string | number,
): RootModelEntity<T> | undefined {
  return store[path[0]][path[1]].entities[id]
    ? store[path[0]][path[1]].entities[id].entity
    : undefined;
}

export function pluckListItemFromRawStoreSlice<T extends keyof RootModelLists>(
  storeSlice: RawStoreListStateSlice<T>,
  path: Path<T>,
  id: string | number,
): RootModelEntity<T> | undefined {
  if (!storeSlice[path[1]] || !storeSlice[path[1]].entities[id]) {
    return undefined;
  }
  return storeSlice[path[1]].entities[id].entity;
}

export function removeEntityById<T extends keyof RootModelLists>(
  ref: Reference<T>,
  state: RawStoreListState[T],
  id: string | number,
): RawStoreListState[T] {
  const data = state[ref.path[1]] || { lists: [], entities: {} };
  const entities = Object.assign({}, data.entities);
  delete entities[id];
  const updatedLists: Lists = {};
  Object.keys(data.lists).forEach(k => {
    updatedLists[k] = data.lists[k].filter(
      el => el.toString() !== id.toString(),
    );
  });

  return Object.assign({}, state, {
    [ref.path[1]]: {
      entities,
      lists: updatedLists,
    },
  });
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/zh/webapp/src/app/@shared/datastore/helpers.ts